// <purpose>The „Nicht hilfreich" act's PURE half (bottom-bar decision, 2026-10-16): the verb, the
// settled reasons, the chooser's words and rows, the slot's face rule and the word a standing
// verdict reads as. Shared by DeclineSlot (both bottom bars) and TicketRowMeta (the Archiv row's
// pill) so the act says the same thing everywhere; no React, no mount needed to pin a rule.</purpose>
import type { CategoryMeta } from '../context/ClassificationSchemaContext';
import type { DeclineInstead, TicketDecline } from '../serverTypes';

export const DECLINE_LABEL = 'Nicht hilfreich';

// Why a settled slot is unpressable — read out of the button's title, the bar's shared idiom.
// `settled` is the settled bar's word for its off-face verb too (Genehmigen settles the row,
// 2026-10-23): the way back from a decision is Neu analysieren with a reason, never a second press.
export const DECLINE_REASON = {
  settled: 'Vorgang bereits entschieden. Zurück über Neu analysieren.',
  declined: 'Bereits als nicht hilfreich entschieden',
  // Verbatim the wording every other paused action uses.
  maintenance: 'Wartungsarbeiten — Aktionen pausiert',
} as const;

export const CHOOSER = {
  heading: 'Was trifft stattdessen zu?',
  // On a row the classifier could not place, the question is the axis value itself — asked with
  // the tenant's SERVED axis noun (Kategorie · Bearbeitung · Marktprozess), never one role's word.
  headingUnclassified: (axisNoun: string) => `Welche ${axisNoun} trifft zu?`,
  byHand: 'Von Hand beantwortet',
  noCase: 'Kein Vorgang',
  noReason: 'Ohne Angabe ablegen',
} as const;

export type DeclineSlotFace = 'on' | 'off';

/** The slot's face from the row alone — pure, so the rule can be read and pinned without a mount:
 *  open (suggestion_ready) is pressable unless maintenance pauses writes; everything else is
 *  settled and says why. (Since "Genehmigen settles the row" the slot is only MOUNTED on open rows —
 *  the settled bar morphs it into Archivieren — so the off faces are the maintenance one and the
 *  mid-flight one in practice; the rule stays complete for every status it may be asked about.) */
export function declineSlotFace(
  status: string, previousStatus: string | null | undefined, maintenance: boolean,
): { face: DeclineSlotFace; title?: string } {
  if (status === 'declined' || (status === 'archived' && previousStatus === 'declined')) return { face: 'off', title: DECLINE_REASON.declined };
  if (status !== 'suggestion_ready') return { face: 'off', title: DECLINE_REASON.settled };
  if (maintenance) return { face: 'off', title: DECLINE_REASON.maintenance };
  return { face: 'on' };
}

/** `hotkey` is the row's keyboard pick, shown as its hint: the first nine axis rows take the digits
 *  1-9, „Von Hand beantwortet" H, „Kein Vorgang" K, „Ohne Angabe ablegen" Enter (a VNB tenant serves
 *  more than nine axis values, so the two fixed answers never depend on their position). An axis row
 *  past the ninth has no hotkey and is picked by click. */
export interface DeclineChooserRow { key: string; label: string; instead: DeclineInstead | null; hotkey: string | null }

/** The chooser's rows and heading for a row — pure. The axis rows are the tenant's served primary
 *  values minus the unclassified bucket (a "true value" is never "could not tell") and minus the
 *  row's own current value (the clerk just said it was not helpful). Order: axis values, by hand,
 *  no case, no reason — except on a row WITHOUT a draft, where „Kein Vorgang" leads (the likeliest
 *  answer for a mail nothing could be written for). On an unclassified row the heading asks for
 *  the axis value outright, with the served axis noun. */
export function declineChooserRows(
  cm: Pick<CategoryMeta, 'categoryKeys' | 'isUnclassified' | 'label' | 'categoryNoun'>,
  row: { primaryAxis?: string | null; noDraft: boolean },
): { heading: string; rows: DeclineChooserRow[] } {
  const current = row.primaryAxis ?? null;
  const axisValues: DeclineChooserRow[] = cm.categoryKeys
    .filter(id => !cm.isUnclassified(id) && id !== current)
    .map((id, i) => ({ key: `primary_axis:${id}`, label: cm.label(id), instead: { kind: 'primary_axis', value: id }, hotkey: i < 9 ? String(i + 1) : null }));
  const byHand: DeclineChooserRow = { key: 'by_hand', label: CHOOSER.byHand, instead: { kind: 'by_hand' }, hotkey: 'H' };
  const noCase: DeclineChooserRow = { key: 'no_case', label: CHOOSER.noCase, instead: { kind: 'no_case' }, hotkey: 'K' };
  const noReason: DeclineChooserRow = { key: 'none', label: CHOOSER.noReason, instead: null, hotkey: 'Enter' };
  const rows = row.noDraft
    ? [noCase, ...axisValues, byHand, noReason]
    : [...axisValues, byHand, noCase, noReason];
  const unclassified = current !== null && cm.isUnclassified(current);
  return { heading: unclassified ? CHOOSER.headingUnclassified(cm.categoryNoun) : CHOOSER.heading, rows };
}

/** The word a standing verdict's "instead" reads as — the served axis label, or the chooser's
 *  own words. Null when the verdict was filed without a reason. Shared by the detail's settled
 *  face and the Archiv row's pill, so the two say the same thing. */
export function declineInsteadWord(cm: Pick<CategoryMeta, 'label'>, decline: TicketDecline | null | undefined): string | null {
  if (!decline || !decline.insteadKind) return null;
  switch (decline.insteadKind) {
    case 'primary_axis': return decline.insteadValue ? cm.label(decline.insteadValue) : null;
    case 'by_hand': return CHOOSER.byHand;
    case 'no_case': return CHOOSER.noCase;
    default: return null;
  }
}

/** The HUE a standing verdict's decision badge wears (small facts on a row, 2026-10-19, A.4): the
 *  served hue of the axis value the clerk chose instead; null when the word is the chooser's own
 *  (Von Hand beantwortet, Kein Vorgang) or absent — the badge then wears its outlined face. */
export function declineInsteadHue(cm: Pick<CategoryMeta, 'color'>, decline: TicketDecline | null | undefined): string | null {
  if (!decline || decline.insteadKind !== 'primary_axis' || !decline.insteadValue) return null;
  return cm.color(decline.insteadValue);
}

